import { ReactNode, useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useTabUsage } from "@/hooks/useTabUsage";

export interface Tab {
	id: string;
	label: string;
	icon: ReactNode;
	badge?: number | string;
}

export interface DynamicTabBarProps {
	tabs: Tab[];
	pinnedIds: string[];
	dynamicSlotCount?: number;
	defaultDynamicIds?: string[];
	activeTabId: string;
	onTabChange: (tabId: string) => void;
	entityType: string;
	userId: string;
	utilityTabIds?: string[];
}

const UTILITY_TABS = ["tasks", "history"];

export function DynamicTabBar({
	tabs,
	pinnedIds,
	dynamicSlotCount = 2,
	defaultDynamicIds = ["documents", "comments"],
	activeTabId,
	onTabChange,
	entityType,
	userId,
	utilityTabIds = UTILITY_TABS,
}: DynamicTabBarProps) {
	const { t } = useTranslation();
	const { trackUsage, getDynamicTabs } = useTabUsage(userId, entityType);
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const moreButtonRef = useRef<HTMLButtonElement>(null);
	const barRef = useRef<HTMLDivElement>(null);
	const [overflowing, setOverflowing] = useState(false);

	const allTabIds = tabs.map((tab) => tab.id);
	const dynamicTabIds = getDynamicTabs(allTabIds, pinnedIds, dynamicSlotCount, defaultDynamicIds);

	const pinnedTabs = pinnedIds.map((id) => tabs.find((t) => t.id === id)).filter(Boolean) as Tab[];
	const dynamicTabs = dynamicTabIds.map((id) => tabs.find((t) => t.id === id)).filter(Boolean) as Tab[];
	const visibleIds = new Set([...pinnedIds, ...(overflowing ? [] : dynamicTabIds)]);
	const dropdownTabs = tabs.filter((tab) => !visibleIds.has(tab.id));

	const mainDropdownTabs = dropdownTabs.filter((tab) => !utilityTabIds.includes(tab.id));
	const utilityDropdownTabs = dropdownTabs.filter((tab) => utilityTabIds.includes(tab.id));

	// Detect overflow to collapse dynamic tabs on narrow screens
	useEffect(() => {
		const el = barRef.current;
		if (!el) return;

		const check = () => {
			setOverflowing(el.scrollWidth > el.clientWidth);
		};

		check();
		const observer = new ResizeObserver(check);
		observer.observe(el);
		return () => observer.disconnect();
	}, [dynamicTabIds.join(",")]);

	const handleTabClick = useCallback(
		(tabId: string) => {
			trackUsage(tabId);
			onTabChange(tabId);
			setDropdownOpen(false);
		},
		[trackUsage, onTabChange]
	);

	// Close dropdown on outside click
	useEffect(() => {
		if (!dropdownOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node) &&
				moreButtonRef.current &&
				!moreButtonRef.current.contains(e.target as Node)
			)
				setDropdownOpen(false);
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [dropdownOpen]);

	// Close dropdown on Escape
	useEffect(() => {
		if (!dropdownOpen) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setDropdownOpen(false);
				moreButtonRef.current?.focus();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [dropdownOpen]);

	// Check if active tab is in the dropdown (not visible in bar)
	const activeInDropdown = dropdownTabs.some((tab) => tab.id === activeTabId);

	return (
		<div className="relative mb-4 border-b border-border/40">
			<div ref={barRef} className="flex items-center h-11" role="tablist">
				{/* Pinned tabs */}
				{pinnedTabs.map((tab) => (
					<TabButton
						key={tab.id}
						tab={tab}
						isActive={activeTabId === tab.id}
						onClick={() => handleTabClick(tab.id)}
						compact={overflowing}
					/>
				))}

				{/* Separator + Dynamic tabs (hidden when overflowing) */}
				{!overflowing && dynamicTabs.length > 0 && (
					<>
						<Separator />
						{dynamicTabs.map((tab) => (
							<TabButton
								key={tab.id}
								tab={tab}
								isActive={activeTabId === tab.id}
								isDynamic
								onClick={() => handleTabClick(tab.id)}
							/>
						))}
					</>
				)}

				{/* More button + dropdown — inline right after tabs */}
				{dropdownTabs.length > 0 && (
					<>
						<Separator />
						<div className="relative flex-shrink-0 flex items-center h-11">
							<button
								ref={moreButtonRef}
								onClick={() => setDropdownOpen((prev) => !prev)}
								className={cn(
									"flex items-center gap-1.5 px-3.5 h-11 text-sm font-medium",
									"text-muted-foreground hover:bg-muted transition-colors",
									(dropdownOpen || activeInDropdown) && "text-blue-500 dark:text-blue-400"
								)}
								aria-haspopup="true"
								aria-expanded={dropdownOpen}
							>
								<span>{t("candidates.tabs.more", "More")}</span>
								<ChevronDown className={cn("h-4 w-4 transition-transform", dropdownOpen && "rotate-180")} />
							</button>

							{dropdownOpen && (
								<div
									ref={dropdownRef}
									className="absolute left-0 top-full mt-1 z-50 min-w-[200px] bg-popover border border-border rounded-sm p-1 shadow-sm"
									role="menu"
								>
									{mainDropdownTabs.map((tab) => (
										<DropdownItem
											key={tab.id}
											tab={tab}
											isActive={activeTabId === tab.id}
											onClick={() => handleTabClick(tab.id)}
										/>
									))}

									{mainDropdownTabs.length > 0 && utilityDropdownTabs.length > 0 && (
										<div className="my-1 mx-2 border-t border-border" />
									)}

									{utilityDropdownTabs.map((tab) => (
										<DropdownItem
											key={tab.id}
											tab={tab}
											isActive={activeTabId === tab.id}
											onClick={() => handleTabClick(tab.id)}
										/>
									))}
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
}

function TabButton({
	tab,
	isActive,
	isDynamic,
	onClick,
	compact,
}: {
	tab: Tab;
	isActive: boolean;
	isDynamic?: boolean;
	onClick: () => void;
	compact?: boolean;
}) {
	return (
		<button
			role="tab"
			aria-selected={isActive}
			aria-label={tab.label}
			onClick={onClick}
			data-tour-tab={tab.id}
			className={cn(
				"flex items-center gap-1.5 h-11 text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0",
				compact ? "px-2.5" : "px-3.5",
				"hover:bg-muted",
				isActive && "text-blue-500 dark:text-blue-400 border-b-2 border-blue-500 dark:border-blue-400",
				!isActive && "text-muted-foreground",
				!isActive && isDynamic && "opacity-60"
			)}
		>
			<span className="h-4 w-4 flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4">
				{tab.icon}
			</span>
			<span className={compact ? "hidden sm:inline" : ""}>{tab.label}</span>
			{tab.badge !== undefined && tab.badge !== 0 && (
				<span className="h-4 min-w-4 px-1 text-[10px] rounded-sm bg-muted flex items-center justify-center font-medium">
					{tab.badge}
				</span>
			)}
		</button>
	);
}

function DropdownItem({
	tab,
	isActive,
	onClick,
}: {
	tab: Tab;
	isActive: boolean;
	onClick: () => void;
}) {
	return (
		<button
			role="menuitem"
			onClick={onClick}
			className={cn(
				"w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
				"hover:bg-muted",
				isActive ? "text-blue-500 dark:text-blue-400 font-medium" : "text-foreground/70"
			)}
		>
			<span className="h-3.5 w-3.5 flex-shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">
				{tab.icon}
			</span>
			<span>{tab.label}</span>
			{tab.badge !== undefined && tab.badge !== 0 && (
				<span className="h-4 min-w-4 px-1 text-[10px] rounded-sm bg-muted flex items-center justify-center font-medium">
					{tab.badge}
				</span>
			)}
		</button>
	);
}

function Separator() {
	return (
		<div className="h-5 w-px bg-border/60 dark:bg-border mx-3 flex-shrink-0" />
	);
}
